import { config } from '../../../../../config';
import DigiobcanApi from '../../../../api/DigiobcanApi';
import type { CategoryUser } from '../../../../api/DigiobcanApi';
import FormAutocompleteField from '../../../../components/form/FormAutocompleteField';
import FormTextField from '../../../../components/form/FormTextField';
import { getReportCategories } from '../../../../features/queries/getReportCategories';
import { getReportCategoryDetail } from '../../../../features/queries/getReportCategoryDetail';
import { getReportCategoryUsers } from '../../../../features/queries/getReportCategoryUsers';
import { useManagedReportCategoryIds } from '../../../../features/queries/meGetReportCategories';
import { entityId, shortId } from '../../../../lib/ids';
import { QueryNameSpace, useM, useQ } from '../../../../lib/query';
import { useLayout } from '../../../../providers/LayoutProvider';
import { useLogger } from '../../../../providers/LoggerProvider';
import { useResponsive } from '../../../../providers/ResponsiveProvider';
import { __, required, toastError, toastSuccess } from '../../../../utils';
import { PRIMARY_SWITCH_SX } from '../../podnety/ReportDetailAdminPage/DecisionSwitch';
import AddCategoryUserDialog from './AddCategoryUserDialog';
import DeleteCategoryUserDialog from './DeleteCategoryUserDialog';
import AddIcon from '@mui/icons-material/Add';
import { useAuth, useAuthRoles } from '@presov.sk/presov-auth-react';
import {
  Box,
  Button,
  Card,
  DataTable,
  EmptyState,
  Stack,
  Switch,
  SwitchField,
  Tooltip,
  Typography,
  type Column,
} from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Field, Form } from 'react-final-form';
import { useParams } from 'react-router';

/**
 * Šírka ľavého stĺpca s formulárom. Formulár má tri polia a nemá dôvod rásť
 * s oknom; zvyšok patrí zoznamu osôb, ktorý má tri stĺpce textu.
 */
const FORM_COLUMN_WIDTH = 420;

type CategoryUserRow = {
  id: string;
  fullName: string;
  email: string;
  manager: boolean;
  can_approve: boolean;
  requires_approval: boolean;
  can_assign: boolean;
};

type MemberFlag =
  | 'manager'
  | 'can_approve'
  | 'requires_approval'
  | 'can_assign';

/**
 * Popis príznakov členstva - do hlavičky stĺpca a do tooltipu. Sú to
 * nezávislé prepínače, nie jedna rola: správca nemusí schvaľovať a nováčik,
 * ktorému sa vyjadrenia schvaľujú, je v kategórii rovnako ako ostatní.
 *
 * „Rozdeľuje" má zmysel len v kategórii, ktorá funguje na prideľovaní -
 * inde sa jeho stĺpec nekreslí (`onlyWithAssignment`).
 */
const MEMBER_FLAGS: {
  key: MemberFlag;
  label: string;
  hint: string;
  onlyWithAssignment?: boolean;
}[] = [
  {
    key: 'manager',
    label: __('Správca'),
    hint: __(
      'Mení členov kategórie a ich nastavenia. Schvaľuje aj rozdeľuje vždy.',
    ),
  },
  {
    key: 'can_approve',
    label: __('Schvaľuje'),
    hint: __('Schvaľuje vyjadrenia kolegov, ktorým sa schvaľujú.'),
  },
  {
    key: 'requires_approval',
    label: __('Vyžaduje schválenie'),
    hint: __(
      'Jeho vyjadrenia sa občanovi zverejnia až po schválení. Nález sa vyrieši až schválením finálneho vyjadrenia.',
    ),
  },
  {
    key: 'can_assign',
    label: __('Rozdeľuje'),
    hint: __(
      'Dostáva notifikácie o nových podnetoch, vidí všetky a prideľuje ich kolegom.',
    ),
    onlyWithAssignment: true,
  },
];

/**
 * „Vyžaduje schválenie" sa vylučuje so „Schvaľuje" aj so „Správca" - správca
 * smie schvaľovať, takže schválenie sám potrebovať nemôže. API takú
 * kombináciu odmieta (`INVALID_CATEGORY_USER_FLAGS`), preto zapnutie role,
 * ktorá schvaľuje, schválenie vypne samo a úradník nemusí prepínať dvakrát.
 *
 * Opačným smerom len pri „Schvaľuje": zapnúť schválenie správcovi by znamenalo
 * potichu mu zobrať správu kategórie, preto je u správcu ten prepínač
 * neaktívny a tooltip povie prečo.
 *
 * Správca schvaľuje aj rozdeľuje vždy, preto mu zapnutie správcu zapne aj
 * „Schvaľuje" a „Rozdeľuje" - uložia sa, nie len vykreslia (API ich pri
 * správcovi drží zapnuté samo). Po odobratí správcu zostanú, kým ich niekto
 * vedome nevypne.
 */
const flagChange = (
  flag: MemberFlag,
  checked: boolean,
): Partial<Record<MemberFlag, boolean>> => {
  if (checked && flag === 'manager') {
    return {
      manager: true,
      can_approve: true,
      can_assign: true,
      requires_approval: false,
    };
  }
  if (checked && flag === 'can_approve') {
    return { can_approve: true, requires_approval: false };
  }
  if (checked && flag === 'requires_approval') {
    return { requires_approval: true, can_approve: false };
  }
  return { [flag]: checked };
};

/**
 * Detail kategórie podnetov: naľavo jej údaje, napravo ľudia, ktorí ju riešia.
 *
 * Nahradil stránku `/zodpovedne-osoby`, ktorá vedela **len** zoznam osôb -
 * kategória sa upravovala v dialógu nad zoznamom kategórií, takže úprava
 * a priradenie osôb boli dve nesúvisiace obrazovky nad tým istým záznamom.
 * Zoznam osôb bol navyše jediná tabuľka v administrácii postavená na
 * holom MUI `Table` namiesto `DataTable` z knižnice, takže sa správal inak
 * než všetky ostatné (žiadne skeletony, iný prázdny stav, iné karty na
 * mobile).
 */
const ReportCategoryAdminPage: React.FC = () => {
  const { categoryId: categoryIdParam } = useParams() as {
    categoryId: string;
  };
  const categoryId = entityId(categoryIdParam, 'category');
  const queryClient = useQueryClient();
  const { logger } = useLogger();
  const { setPageTitle } = useLayout();
  const { hasPermission } = useAuthRoles();
  const { authUser } = useAuth();
  const { isLg, isMd } = useResponsive();

  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showAddDialog, setShowAddDialog] = React.useState(false);

  const canManage = React.useMemo(
    () =>
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:report-categories:manage',
      }),
    [hasPermission],
  );

  // Správca kategórie smie spravovať jej členov bez celomestského
  // `users:manage` - to je celý zmysel tej roly. API to kontroluje rovnako.
  const managedCategoryIds = useManagedReportCategoryIds();
  const hasUsersManage = React.useMemo(
    () =>
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:report-categories:users:manage',
      }),
    [hasPermission],
  );
  const canManageUsers =
    hasUsersManage || managedCategoryIds.includes(categoryId);

  // Správca si nesmie sám vziať správcovstvo - ani prepínačom, ani odobratím
  // z kategórie (API: 403 `FORBIDDEN_OWN_MANAGER_REMOVAL`). Kategória by
  // zostala bez správcu a vrátiť ho vie len `users:manage`, ktorý kategóriu
  // spravuje oprávnením, a preto si to smie urobiť aj sám.
  const ownId = authUser?.clientAccountId
    ? shortId(authUser.clientAccountId)
    : undefined;
  const isOwnRow = React.useCallback(
    (userId: string) => !hasUsersManage && !!ownId && shortId(userId) === ownId,
    [hasUsersManage, ownId],
  );
  const selectionHasSelf = selectedRows.some(isOwnRow);

  const { data: category, queryKey: categoryQueryKey } = useQ(
    getReportCategoryDetail,
    { categoryId },
  );

  // Ponuka nadradených kategórií. Berie sa plochý zoznam, nie strom - do
  // `options` idú aj podkategórie a strom by sa musel najprv rozbaliť.
  const { data: allCategories = [] } = useQ(getReportCategories, {
    view: 'flat',
  });

  const {
    data: categoryUsers = [],
    isLoading: isLoadingCategoryUsers,
    isError: isErrorCategoryUsers,
    queryKey: userQueryKey,
  } = useQ(getReportCategoryUsers, { categoryId });

  React.useEffect(() => {
    if (isErrorCategoryUsers) {
      toastError(__('Nepodarilo sa načítať používateľov kategórie.'));
    }
  }, [isErrorCategoryUsers]);

  React.useEffect(() => {
    if (category?.name) {
      setPageTitle(category.name);
    }
  }, [category, setPageTitle]);

  const updateCategoryMutation = useM(
    (values: {
      name: string;
      description?: string;
      parent_id?: string;
      assignment?: boolean;
    }) => DigiobcanApi.updateReportCategory(categoryId, {}, values),
    {
      onSuccess: (updated) => {
        toastSuccess(__('Kategória bola aktualizovaná'));
        queryClient.setQueryData(categoryQueryKey, updated);
        // Zoznam kategórií nesie názvy aj cesty, takže po premenovaní
        // prestáva sedieť celý strom, nie len tento riadok.
        queryClient.invalidateQueries({
          queryKey: [QueryNameSpace.getReportCategories],
        });
        // Prepínač prideľovania nesie aj `meGetReportCategories` - podľa neho
        // sa v zozname podnetov ponúka filter „Pridelené mne".
        queryClient.invalidateQueries({
          queryKey: [QueryNameSpace.meGetReportCategories],
        });
      },
      onError: (e, variables) => {
        logger.error('Error updating report category', {
          action: 'updateReportCategory',
          error: e,
          data: { categoryId, ...variables },
        });
        toastError(__('Niečo sa pokazilo'));
      },
    },
  );

  const handleSave = async (values) => {
    if (!navigator.onLine) {
      toastError(__('Internetové pripojenie zlyhalo.'));
      return;
    }

    try {
      await updateCategoryMutation.mutateAsync({
        name: values.name,
        description: values.description,
        parent_id: values.parentId || '',
        assignment: !!values.assignment,
      });
    } catch {
      // Empty catch because mutation handles errors
    }
  };

  const updateMemberMutation = useM(
    ({
      userId,
      changes,
    }: {
      userId: string;
      changes: Partial<Record<MemberFlag, boolean>>;
    }) =>
      DigiobcanApi.updateReportCategoryUser(categoryId, userId, {}, changes),
    {
      onSuccess: (updated) => {
        queryClient.setQueryData(userQueryKey, (old: CategoryUser[] = []) =>
          old.map((user) => (user.id === updated.id ? updated : user)),
        );
        // Vlastné príznaky nesie aj `meGetReportCategories` - filtre
        // „Na schválenie" a „Pridelené mne" aj tlačidlá v detaile nálezu sa
        // podľa nich riadia.
        queryClient.invalidateQueries({
          queryKey: [QueryNameSpace.meGetReportCategories],
        });
      },
      onError: (e, variables) => {
        logger.error('Error updating report category member', {
          action: 'updateReportCategoryUser',
          error: e,
          data: { categoryId, ...variables },
        });
        toastError(
          e.details?.name === 'FORBIDDEN_CATEGORY_USERS_MANAGE'
            ? __('Členov tejto kategórie nespravujete.')
            : __('Nastavenie sa nepodarilo uložiť.'),
        );
      },
    },
  );

  const userRows: CategoryUserRow[] = React.useMemo(
    () =>
      categoryUsers.map((user) => ({
        id: user.id,
        fullName: [user.name, user.surname].filter(Boolean).join(' '),
        email: user.email ?? '',
        manager: !!user.manager,
        can_approve: !!user.can_approve,
        requires_approval: !!user.requires_approval,
        can_assign: !!user.can_assign,
      })),
    [categoryUsers],
  );

  // Kategória prideľuje, ale nemá kto rozdeľovať - nové podnety by nikomu
  // neprišli a obyčajní členovia ich nevidia. Rovnako ako pri schvaľovaní to
  // API nezakazuje, ale obrazovka to musí povedať.
  const assignmentWithoutAssigner =
    !!category?.assignment &&
    !isLoadingCategoryUsers &&
    !userRows.some((user) => user.can_assign || user.manager);

  // Niekomu sa vyjadrenia schvaľujú, ale nemá ich kto schváliť - čakali by
  // navždy. API to nezakazuje (schvaľovateľ sa môže doplniť o chvíľu), ale
  // obrazovka to musí povedať nahlas.
  const approvalWithoutApprover =
    userRows.some((user) => user.requires_approval) &&
    !userRows.some((user) => user.can_approve || user.manager);

  const { mutate: updateMember, isPending: isUpdatingMember } =
    updateMemberMutation;

  const userColumns: Column<CategoryUserRow>[] = React.useMemo(
    () => [
      {
        key: 'id',
        label: __('ID'),
        // Rovnako široký a pevný ako v ostatných tabuľkách: identifikátor má
        // vždy sedem znakov a predpona sa v bunke nekreslí.
        width: '92px',
        minWidth: '92px',
        mono: true,
        render: (user) => shortId(user.id),
      },
      {
        key: 'fullName',
        label: __('Meno a priezvisko'),
        width: '1.6fr',
        minWidth: '180px',
        strong: true,
      },
      {
        key: 'email',
        label: __('E-mail'),
        width: '1.6fr',
        minWidth: '200px',
      },
      ...MEMBER_FLAGS.filter(
        (flag) => !flag.onlyWithAssignment || !!category?.assignment,
      ).map(
        (flag): Column<CategoryUserRow> => ({
          key: flag.key,
          label: flag.label,
          width: '0.9fr',
          minWidth: '120px',
          render: (user) => {
            const blockedByManager =
              flag.key === 'requires_approval' && user.manager;
            // Správca schvaľuje aj rozdeľuje vždy - prepínač je zapnutý
            // a neaktívny, kým je správcom (aj pri starších správcoch, ktorým
            // sa príznak pri menovaní ešte neuložil).
            const impliedByManager =
              (flag.key === 'can_approve' || flag.key === 'can_assign') &&
              user.manager;
            const blockedOwnManager =
              flag.key === 'manager' && user.manager && isOwnRow(user.id);

            return (
              <Tooltip
                title={
                  blockedOwnManager
                    ? __(
                        'Rolu správcu si sám odobrať nemôžete - kategória by zostala bez správcu. Požiadajte o to iného správcu.',
                      )
                    : blockedByManager
                      ? __(
                          'Správca schvaľuje sám, schválenie potrebovať nemôže. Najprv mu vypnite rolu správcu.',
                        )
                      : impliedByManager
                        ? __(
                            'Správca schvaľuje aj rozdeľuje vždy. Vypnúť sa to dá až po odobratí roly správcu.',
                          )
                        : flag.hint
                }
              >
                <span>
                  <Switch
                    size='small'
                    checked={user[flag.key] || impliedByManager}
                    disabled={
                      !canManageUsers ||
                      isUpdatingMember ||
                      blockedByManager ||
                      blockedOwnManager ||
                      impliedByManager
                    }
                    sx={PRIMARY_SWITCH_SX}
                    inputProps={{
                      'aria-label': `${flag.label}: ${user.fullName}`,
                    }}
                    // Riadok má výber; klik na prepínač ho nemá prepnúť.
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) =>
                      updateMember({
                        userId: user.id,
                        changes: flagChange(flag.key, event.target.checked),
                      })
                    }
                  />
                </span>
              </Tooltip>
            );
          },
        }),
      ),
    ],
    [
      canManageUsers,
      isUpdatingMember,
      updateMember,
      isOwnRow,
      category?.assignment,
    ],
  );

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isLg
            ? `minmax(0, ${FORM_COLUMN_WIDTH}px) minmax(0, 1fr)`
            : 'minmax(0, 1fr)',
          gap: 1.5,
          padding: 1.5,
          // `start`, nie `stretch`: formulár nemá dôvod naťahovať sa na výšku
          // zoznamu osôb, ktorý môže mať aj tridsať riadkov.
          alignItems: 'start',
        }}
      >
        <Card sx={{ padding: 2 }}>
          <Typography variant='subtitle2' sx={{ mb: 1.5 }}>
            {__('Údaje kategórie')}
          </Typography>

          <Form
            onSubmit={handleSave}
            // Formulár sa napĺňa až keď dotaz dobehne, preto `key`: bez neho
            // by si `react-final-form` podržal prázdne počiatočné hodnoty
            // a pole by zostalo prázdne aj po načítaní.
            key={category?.category_id ?? 'loading'}
            initialValues={{
              name: category?.name ?? '',
              description: category?.description ?? '',
              parentId: category?.parent_id ?? '',
              assignment: !!category?.assignment,
            }}
          >
            {({ handleSubmit, pristine }) => (
              <form
                onSubmit={handleSubmit}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <FormTextField
                  required
                  disabled={!canManage}
                  label={__('Názov kategórie')}
                  name='name'
                  validate={required}
                  maxLength={120}
                />
                <FormTextField
                  disabled={!canManage}
                  label={__('Popis kategórie')}
                  name='description'
                  multiline
                  rows={3}
                  maxLength={500}
                />
                <FormAutocompleteField
                  name='parentId'
                  disabled={!canManage}
                  label={__('Nadradená kategória')}
                  // Kategória nesmie byť nadradená sama sebe - vznikol by
                  // cyklus, ktorý by strom v zozname zavesil.
                  options={allCategories
                    .filter((item) => item.category_id !== categoryId)
                    .map((item) => ({
                      label: item.name,
                      value: item.category_id,
                    }))}
                />
                <Field name='assignment' type='checkbox'>
                  {({ input }) => (
                    <SwitchField
                      label={__('Kategória funguje na prideľovaní osôb')}
                      description={__(
                        'Nové podnety vidia a notifikácie o nich dostávajú len rozdeľovatelia a správcovia. Ostatní členovia vidia len podnety, ktoré im pridelia.',
                      )}
                      checked={!!input.checked}
                      onChange={(checked) => input.onChange(checked)}
                      disabled={!canManage}
                    />
                  )}
                </Field>

                {canManage ? (
                  <Stack direction='row' justifyContent='flex-end' mt={1.5}>
                    <Button
                      type='submit'
                      variant='dark'
                      size='small'
                      disabled={pristine || updateCategoryMutation.isPending}
                    >
                      {__('Uložiť zmeny')}
                    </Button>
                  </Stack>
                ) : null}
              </form>
            )}
          </Form>
        </Card>

        <Card sx={{ padding: 2 }}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            gap={1}
            sx={{ mb: 1.5 }}
          >
            <Typography variant='subtitle2'>
              {__('Zodpovedné osoby')}
            </Typography>

            <Stack direction='row' gap={1} className='screen-only'>
              {canManageUsers && selectedRows.length ? (
                <Tooltip
                  title={
                    selectionHasSelf
                      ? __(
                          'Vo výbere ste aj vy - seba z kategórie odobrať nemôžete, zostala by bez správcu.',
                        )
                      : ''
                  }
                >
                  <span>
                    <Button
                      size='small'
                      variant='danger'
                      disabled={selectionHasSelf}
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      {`${__('Odobrať')} (${selectedRows.length})`}
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
              {canManageUsers ? (
                <Button
                  size='small'
                  variant='dark'
                  onClick={() => setShowAddDialog(true)}
                >
                  <AddIcon fontSize='small' sx={{ mr: 0.5 }} />
                  {__('Pridať ďalšieho používateľa')}
                </Button>
              ) : null}
            </Stack>
          </Stack>

          {assignmentWithoutAssigner && (
            <Stack
              gap={0.25}
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.warning.surface}`,
                backgroundColor: 'warning.surface',
              }}
            >
              <Typography variant='body2' color='warning.dark' fontWeight={500}>
                {__('Podnety nemá kto rozdeľovať')}
              </Typography>
              <Typography variant='caption' color='warning.dark'>
                {__(
                  'Kategória funguje na prideľovaní, ale nikto v nej nerozdeľuje ani ju nespravuje. Nové podnety tak nikomu neprídu a ostatní členovia ich nevidia.',
                )}
              </Typography>
            </Stack>
          )}

          {approvalWithoutApprover && (
            <Stack
              gap={0.25}
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.warning.surface}`,
                backgroundColor: 'warning.surface',
              }}
            >
              <Typography variant='body2' color='warning.dark' fontWeight={500}>
                {__('Vyjadrenia nemá kto schváliť')}
              </Typography>
              <Typography variant='caption' color='warning.dark'>
                {__(
                  'Niekomu v kategórii sa vyjadrenia schvaľujú, ale nikto ich neschvaľuje ani kategóriu nespravuje. Kým to nikomu nezapnete, budú čakať.',
                )}
              </Typography>
            </Stack>
          )}

          <DataTable
            variant={isMd ? 'table' : 'cards'}
            columns={userColumns}
            rows={userRows}
            rowKey='id'
            selected={canManageUsers ? selectedRows : undefined}
            onSelectionChange={
              canManageUsers
                ? (next) => setSelectedRows(next.map(String))
                : undefined
            }
            selectAllLabel={__('Označiť všetky osoby')}
            selectRowLabel={__('Označiť osobu')}
            loading={isLoadingCategoryUsers}
            loadingRows={3}
            empty={
              <EmptyState
                compact
                icon='list'
                title={__('Kategória nemá žiadne zodpovedné osoby')}
                description={__(
                  'Podnet v tejto kategórii nemá komu prísť na stôl.',
                )}
                actions={
                  canManageUsers ? (
                    <Button
                      variant='outline'
                      size='small'
                      onClick={() => setShowAddDialog(true)}
                    >
                      {__('Pridať ďalšieho používateľa')}
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </Card>
      </Box>

      <AddCategoryUserDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        categoryId={categoryId}
        usedUserIds={categoryUsers.map((user) => user.id)}
        onSuccess={(addedUsers) => {
          // Nový člen má všetky príznaky vypnuté - tak ho API aj zakladá.
          queryClient.setQueryData(userQueryKey, (old: CategoryUser[] = []) => [
            ...old,
            ...addedUsers
              .filter((added) => !old.some((user) => user.id === added.id))
              .map((added) => ({
                ...added,
                manager: false,
                can_approve: false,
                requires_approval: false,
                can_assign: false,
              })),
          ]);
          // Zoznam kategórií kreslí z priradených osôb avatary, takže mu
          // pribudnutý človek zmení riadok.
          queryClient.invalidateQueries({
            queryKey: [QueryNameSpace.getReportCategories],
          });
          setSelectedRows([]);
        }}
      />
      <DeleteCategoryUserDialog
        categoryId={categoryId}
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        selectedIds={selectedRows}
        onSuccess={(deletedUserIds) => {
          queryClient.setQueryData(userQueryKey, (old: CategoryUser[] = []) =>
            old.filter((user) => !deletedUserIds.includes(user.id)),
          );
          queryClient.invalidateQueries({
            queryKey: [QueryNameSpace.getReportCategories],
          });
          setSelectedRows([]);
        }}
      />
    </>
  );
};

export default ReportCategoryAdminPage;
